import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Chip, Typography, Avatar, IconButton, CircularProgress, Button} from 'views/components';
import {Translate, translate} from 'views/module';
import {Mic, MicOff, CheckCircle, Hearing, Schedule, Phone, PhoneDisabled} from 'views/icons';
import {useConversation} from '@elevenlabs/react';

const AiCall = (props) => {
  const {
    agentId,
    onCallStart,
    onCallEnd,
    clientTools = {},
    dynamicVariables = {},
    startCallText = translate('aiCall.AiCall.StartCall'),
    callEndedText = translate('aiCall.AiCall.callEnded'),
  } = props;

  const [connecting, setConnecting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [callDuration, setCallDuration] = React.useState(0);

  const conversation = useConversation({
    micMuted: isMuted,
    clientTools,
    onMessage: () => undefined,
    onError: console.error,
    onDebug: console.debug,
    onConnect: () => {
      setConnecting(false);
      if (onCallStart) onCallStart();
      setCallDuration(0);
    },
    onDisconnect: () => {
      postInterview();
    },
  });

  const connected = conversation.status === 'connected';
  const agentSpeaking = conversation.isSpeaking;

  // Handle mute toggle
  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  // Get status chip props
  const getStatusChipProps = () => {
    if (connecting) {
      return {
        color: 'warning',
        label: translate('aiCall.AiCall.Connecting'),
        icon: <CircularProgress size={16} />,
      };
    } else if (connected) {
      if (agentSpeaking) {
        return {
          color: 'primary',
          label: translate('aiCall.AiCall.AISpeaking'),
          icon: <img src="/logo/quilt-icon.png" alt="AI Avatar" style={{width: 16, height: 16}} />,
        };
      } else {
        return {
          color: 'info',
          label: translate('aiCall.AiCall.AIListening'),
          icon: <Hearing />,
        };
      }
    } else if (done) {
      return {
        color: 'success',
        label: callEndedText,
        icon: <CheckCircle />,
      };
    } else {
      return {
        color: 'default',
        label: translate('aiCall.AiCall.ReadyToStart'),
        icon: <Schedule />,
      };
    }
  };

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const statusProps = getStatusChipProps();

  // Timer for call duration
  React.useEffect(() => {
    let interval = null;
    if (connected) {
      interval = setInterval(() => {
        setCallDuration((duration) => duration + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [connected]);

  // Handle starting the interview
  const handleStartInterview = () => {
    setConnecting(true);
    // Simulate connection delay
    conversation.startSession({
      agentId,
      dynamicVariables,
      clientTools,
    });
  };

  // Handle ending the interview
  const handleEndInterview = () => {
    conversation.endSession();
    onCallEnd();
    postInterview();
  };

  const postInterview = () => {
    // Handle post-interview actions
    onCallEnd();
    setIsMuted(false);
    setDone(true);
  };

  return (
    <Stack alignItems="center">
      {/* AI Avatar - shown when connected */}
      {
        connected && (
          <Avatar
            sx={{
              'width': 64,
              'height': 64,
              'bgcolor': agentSpeaking ? 'primary.main' : 'info.main',
              'mb': 1,
              'transition': 'all 0.3s ease',
              'animation': agentSpeaking ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  'transform': 'scale(1)',
                  'opacity': 1,
                },
                '50%': {
                  'transform': 'scale(1.05)',
                  'opacity': 0.8,
                },
                '100%': {
                  'transform': 'scale(1)',
                  'opacity': 1,
                },
              },
            }}
          >
            <img src="/logo/quilt-icon.png" alt="AI Avatar" style={{width: 32, height: 32}} />
          </Avatar>
        )
      }

      {/* Status Chip */}
      <Stack spacing={1} alignItems="center">
        <Chip
          icon={statusProps.icon}
          label={statusProps.label}
          color={statusProps.color}
          variant="filled"
          size="medium"
        />
        {
          connected && (
            <Stack direction="row" spacing={1} justifyContent="center">
              <IconButton
                color={isMuted ? 'error' : 'primary'}
                onClick={handleMuteToggle}
                size="small"
                sx={{
                  'bgcolor': isMuted ? 'error.main' : 'primary.main',
                  'color': 'white',
                  '&:hover': {
                    'bgcolor': isMuted ? 'error.dark' : 'primary.dark',
                  },
                }}
              >
                {isMuted ? <MicOff /> : <Mic />}
              </IconButton>
              <IconButton
                color="primary"
                onClick={handleEndInterview}
                size="small"
                sx={{
                  'bgcolor': 'error.main',
                  'color': 'white',
                  '&:hover': {
                    'bgcolor': 'error.dark',
                  },
                }}
              >
                <PhoneDisabled />
              </IconButton>
            </Stack>
          )
        }
        {
          !connected && !done && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              size="small"
              startIcon={<Phone />}
              onClick={handleStartInterview}
              sx={{mt: 2, borderRadius: 10}}
            >
              {startCallText}
            </Button>
          )
        }
      </Stack>

      {/* Call Duration */}
      {
        (connected || done) && (
          <Typography variant="h6" color="text.secondary">
            <Translate expression='aiCall.AiCall.Duration' substitute={{duration: formatDuration(callDuration)}}/>
          </Typography>
        )
      }
    </Stack>
  );
};

AiCall.propTypes = {
  agentId: PropTypes.string.isRequired,
  clientTools: PropTypes.object,
  dynamicVariables: PropTypes.object,

  onCallStart: PropTypes.func,
  onCallEnd: PropTypes.func,

  startCallText: PropTypes.string,
  callEndedText: PropTypes.string,
};

export default AiCall;
